import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { Handle, flexStyles, positionStyles } from "ui";
import { BlocksContext } from "../../BlocksContext";
import { InlineImage } from "../../components/InlineImage";
import { useResize } from "../../hooks/useResize";
import { Editor } from "./Editor";

// Handle width with gap in pixels
const HandleWidth = 16;

interface TwoImagesBlockProps {
  leftImage: string;
  rightImage: string;
  leftAlt: string;
  rightAlt: string;
  leftUrl: string;
  rightUrl: string;
  leftCaption: string;
  rightCaption: string;
  widthPercentage: number;
  updateBlock: (
    props: Partial<{
      leftImage: string;
      rightImage: string;
      leftAlt: string;
      rightAlt: string;
      leftUrl: string;
      rightUrl: string;
      leftCaption: string;
      rightCaption: string;
      widthPercentage: number;
    }>,
  ) => void;
}

export function TwoImagesBlock({
  leftImage,
  rightImage,
  leftAlt,
  rightAlt,
  leftUrl,
  rightUrl,
  leftCaption,
  rightCaption,
  widthPercentage = 50,
  updateBlock,
}: TwoImagesBlockProps) {
  const { NewImage, EditPopover } = useContext(BlocksContext);
  const leftRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { onMouseDown, onTouchStart, width } = useResize(leftRef, { maxWidth: null });
  const [isLeftEditorOpen, setIsLeftEditorOpen] = useState(false);
  const [isRightEditorOpen, setIsRightEditorOpen] = useState(false);

  // Calculate widthPercentage based on width
  useEffect(() => {
    if (containerRef.current && leftRef.current && width !== Number.POSITIVE_INFINITY) {
      const containerWidth = containerRef.current.clientWidth;
      const newPercentage = Math.round(((width + HandleWidth / 2) / containerWidth) * 100);

      if (newPercentage !== widthPercentage && newPercentage > 20 && newPercentage < 80) {
        updateBlock({ widthPercentage: newPercentage });
      }
    }
  }, [width, widthPercentage, updateBlock]);

  const handleLeftImageUpdate = (imageUrl: string) => {
    updateBlock({ leftImage: imageUrl });
  };

  const handleRightImageUpdate = (imageUrl: string) => {
    updateBlock({ rightImage: imageUrl });
  };

  const handleLeftUpdate = (
    values: Partial<{
      alt: string;
      url: string;
      caption: string;
    }>,
  ) => {
    const updateProps: Partial<typeof updateBlock extends (props: infer P) => void ? P : never> = {};
    if (values.alt !== undefined) updateProps.leftAlt = values.alt;
    if (values.url !== undefined) updateProps.leftUrl = values.url;
    if (values.caption !== undefined) updateProps.leftCaption = values.caption;
    updateBlock(updateProps);
  };

  const handleRightUpdate = (
    values: Partial<{
      alt: string;
      url: string;
      caption: string;
    }>,
  ) => {
    const updateProps: Partial<typeof updateBlock extends (props: infer P) => void ? P : never> = {};
    if (values.alt !== undefined) updateProps.rightAlt = values.alt;
    if (values.url !== undefined) updateProps.rightUrl = values.url;
    if (values.caption !== undefined) updateProps.rightCaption = values.caption;
    updateBlock(updateProps);
  };

  return (
    <div ref={containerRef} className={classNames(flexStyles.horiz, flexStyles.gap031)}>
      <div ref={leftRef} style={{ flexBasis: `${widthPercentage}%` }} className={classNames(positionStyles.relative)}>
        {leftImage ? (
          <>
            <InlineImage image={leftImage} alt={leftAlt} caption={leftCaption} />
            <EditPopover
              isOpen={isLeftEditorOpen}
              onOpen={() => setIsLeftEditorOpen(!isLeftEditorOpen)}
              showEditButton={!!leftImage}
              editor={
                <Editor
                  alt={leftAlt}
                  url={leftUrl}
                  caption={leftCaption}
                  onUpdate={handleLeftUpdate}
                  onClose={() => setIsLeftEditorOpen(false)}
                />
              }
            />
          </>
        ) : (
          <NewImage onUpload={handleLeftImageUpdate} />
        )}
      </div>

      <Handle
        className={flexStyles.alignSelfCenter}
        onMouseDown={(event) => onMouseDown(event, "positive")}
        onTouchStart={(event) => onTouchStart(event, "positive")}
      />

      <div style={{ flexBasis: `${100 - widthPercentage}%` }} className={classNames(positionStyles.relative)}>
        {rightImage ? (
          <>
            <InlineImage image={rightImage} alt={rightAlt} caption={rightCaption} />
            <EditPopover
              isOpen={isRightEditorOpen}
              onOpen={() => setIsRightEditorOpen(!isRightEditorOpen)}
              showEditButton={!!rightImage}
              editor={
                <Editor
                  alt={rightAlt}
                  url={rightUrl}
                  caption={rightCaption}
                  onUpdate={handleRightUpdate}
                  onClose={() => setIsRightEditorOpen(false)}
                />
              }
            />
          </>
        ) : (
          <NewImage onUpload={handleRightImageUpdate} />
        )}
      </div>
    </div>
  );
}
